import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
    Paper,
    Grid,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Typography,
    Box,
    MenuItem,
    Divider,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import useNotification from "../../common/Notification";
import { WB_SX } from "../../../ui/ux/uiTokens";
import HealthBadge from "../shared/HealthBadge";
import SubscriptionFields from "../shared/SubscriptionFields";
import {
    getCredits,
    updateCredits,
    getWbConfig,
    createWbConfig,
    updateWbConfig,
    getUserSubscriptions,
    updateSubscription,
    getUserServiceStatus,
    updateUserRoles,
} from "../../../api/superAdminApi";
import { parseUserRoles } from "../../UserManagement/roleLabels";

const WB_ROLES = ["WB-ADMIN", "WB-USER", "WB-AGENT", "WB-CHAT"];

const AdminWhatsappBusinessPage = () => {
    const { user, reloadUser } = useOutletContext();
    const notify = useNotification();
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [wbConfig, setWbConfig] = useState(null);
    const [wbForm, setWbForm] = useState({
        business_id: "", waba_id: "", app_id: "", phone_number_id: "", whatsapp_phone_no: "",
        status: "INACTIVE", process_status: "INACTIVE", automation_enabled: false,
        bwc: "", dbwc: "", adbwc: "", bot_credits_count: "",
    });
    const [subscriptions, setSubscriptions] = useState(null);
    const [serviceStatus, setServiceStatus] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [creditData, cfg, subs, status] = await Promise.all([
                getCredits(user.id),
                getWbConfig(user.id),
                getUserSubscriptions(user.id).catch(() => null),
                getUserServiceStatus(user.id).catch(() => null),
            ]);
            const wb = creditData?.whatsapp_business || {};
            setWbConfig(cfg);
            setWbForm({
                business_id: cfg?.business_id || "",
                waba_id: cfg?.waba_id || "",
                app_id: cfg?.app_id || "",
                phone_number_id: cfg?.phone_number_id || "",
                whatsapp_phone_no: cfg?.whatsapp_phone_no || "",
                status: cfg?.status || "INACTIVE",
                process_status: cfg?.process_status || "INACTIVE",
                automation_enabled: !!cfg?.automation_enabled,
                bwc: wb.bwc != null ? String(wb.bwc) : "",
                dbwc: wb.dbwc != null ? String(wb.dbwc) : "",
                adbwc: wb.adbwc != null ? String(wb.adbwc) : "",
                bot_credits_count: wb.bot_credits_count != null ? String(wb.bot_credits_count) : "",
            });
            setSubscriptions(subs);
            setServiceStatus(status);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user.id]);

    useEffect(() => { load(); }, [load]);

    const handleSave = async () => {
        setSaving(true);
        setError("");
        try {
            const configPayload = {
                business_id: wbForm.business_id || undefined,
                waba_id: wbForm.waba_id || undefined,
                app_id: wbForm.app_id || undefined,
                phone_number_id: wbForm.phone_number_id || undefined,
                whatsapp_phone_no: wbForm.whatsapp_phone_no || undefined,
                status: wbForm.status,
                process_status: wbForm.process_status,
                automation_enabled: wbForm.automation_enabled,
            };

            const hasConfigFields = wbForm.waba_id || wbForm.phone_number_id || wbForm.whatsapp_phone_no || wbForm.business_id;

            if (hasConfigFields) {
                if (wbConfig) {
                    await updateWbConfig(user.id, configPayload);
                } else {
                    await createWbConfig({ ...configPayload, user_id: user.id });
                }
            }

            const creditPayload = { service: "whatsapp_business" };
            let hasCredit = false;
            ["bwc", "dbwc", "adbwc", "bot_credits_count"].forEach((field) => {
                if (wbForm[field] !== "") {
                    creditPayload[field] = parseInt(wbForm[field], 10);
                    hasCredit = true;
                }
            });
            if (hasCredit) await updateCredits(user.id, creditPayload);

            const userRoles = parseUserRoles(user.user_role);
            const hasWbRole = WB_ROLES.some((r) => userRoles.includes(r));
            if (!hasWbRole && (hasConfigFields || hasCredit)) {
                const roles = [...userRoles, "WB-USER"];
                await updateUserRoles(user.id, roles, "replace");
            }

            if (!hasConfigFields && !hasCredit) {
                setError("Add configuration fields or credits to save.");
                return;
            }

            notify(wbConfig ? "WhatsApp Business updated" : "WhatsApp Business created", "success");
            reloadUser();
            load();
        } catch (err) {
            setError(err.message);
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleSubscriptionSave = async (service, payload) => {
        setSaving(true);
        try {
            await updateSubscription(user.id, service, payload);
            notify("Subscription updated", "success");
            setSubscriptions(await getUserSubscriptions(user.id));
        } catch (err) {
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>;
    }

    return (
        <Paper elevation={0} sx={{ ...WB_SX.card, p: 3 }}>
            {!wbConfig && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    No WhatsApp Business service configured yet. Add Meta API details and save to create one.
                </Alert>
            )}
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            {serviceStatus?.whatsapp_business && (
                <Box sx={{ mb: 2 }}><HealthBadge healthy={serviceStatus.whatsapp_business.is_healthy} /></Box>
            )}

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Meta API configuration</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {[["Business ID", "business_id"], ["WABA ID", "waba_id"], ["App ID", "app_id"], ["Phone number ID", "phone_number_id"], ["WhatsApp phone", "whatsapp_phone_no"]].map(([label, name]) => (
                    <Grid item xs={12} sm={6} key={name}>
                        <TextField fullWidth size="small" label={label} value={wbForm[name]} onChange={(e) => setWbForm((f) => ({ ...f, [name]: e.target.value }))} />
                    </Grid>
                ))}
                <Grid item xs={6}>
                    <TextField select fullWidth size="small" label="Config status" value={wbForm.status} onChange={(e) => setWbForm((f) => ({ ...f, status: e.target.value }))}>
                        <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                        <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                    </TextField>
                </Grid>
                <Grid item xs={6}>
                    <TextField select fullWidth size="small" label="Process status" value={wbForm.process_status} onChange={(e) => setWbForm((f) => ({ ...f, process_status: e.target.value }))}>
                        <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                        <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                    </TextField>
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel control={<Switch checked={wbForm.automation_enabled} onChange={(e) => setWbForm((f) => ({ ...f, automation_enabled: e.target.checked }))} />} label="Automation enabled" />
                </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>WhatsApp Business credits</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {["bwc", "dbwc", "adbwc", "bot_credits_count"].map((field) => (
                    <Grid item xs={12} sm={3} key={field}>
                        <TextField fullWidth size="small" label={field.toUpperCase()} type="number" value={wbForm[field]} onChange={(e) => setWbForm((f) => ({ ...f, [field]: e.target.value }))} inputProps={{ min: 0 }} />
                    </Grid>
                ))}
            </Grid>

            <SubscriptionFields service="whatsapp_business" sub={subscriptions?.whatsapp_business} onSave={handleSubscriptionSave} saving={saving} />

            <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />} onClick={handleSave} disabled={saving} sx={{ ...WB_SX.primaryButton, mt: 3 }}>
                {wbConfig ? "Save WhatsApp Business" : "Create WhatsApp Business service"}
            </Button>
        </Paper>
    );
};

export default AdminWhatsappBusinessPage;
